'use client'

import { useTheme } from 'next-themes'
import dynamic from 'next/dynamic'
import 'react-markdown-editor-lite/lib/index.css'
import MarkdownIt from 'markdown-it'

const MdEditor = dynamic(() => import('react-markdown-editor-lite'), {
  ssr: false,
  loading: () => (
    <div className="w-full border border-border rounded-lg p-4 bg-background">
      <div className="animate-pulse space-y-4">
        <div className="h-10 bg-muted rounded"></div>
        <div className="h-64 bg-muted rounded"></div>
      </div>
    </div>
  ),
})

interface MarkdownEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  height?: number
}

const mdParser = new MarkdownIt()

export function MarkdownEditor({ value, onChange, placeholder = 'Enter text...', height = 400 }: MarkdownEditorProps) {
  const { resolvedTheme } = useTheme()

  const handleEditorChange = ({ text }: { html: string; text: string }) => {
    onChange(text)
  }

  return (
    <div className="markdown-editor-wrapper">
      {/* Preload font icons */}
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css"
        integrity="sha512-1ycn6IcaQQ40/MKBW2W4Rhis/DbILU74C1vSrLJxCq57o941Ym01SwNsOMqvEBFlcgUa6xLiPY/NS5R+E6ztJQ=="
        crossOrigin="anonymous"
        referrerPolicy="no-referrer"
      />
      <MdEditor
        value={value}
        style={{ height: `${height}px` }}
        renderHTML={(text) => mdParser.render(text)}
        onChange={handleEditorChange}
        placeholder={placeholder}
        view={{ menu: true, md: true, html: false }}
        canView={{ menu: true, md: true, html: true, both: false, fullScreen: true, hideMenu: true }}
      />
      <style jsx global>{`
        /* Base editor styles */
        .markdown-editor-wrapper .rc-md-editor {
          border: 1px solid hsl(var(--border));
          border-radius: 0.5rem;
          background: hsl(var(--background));
          color: hsl(var(--foreground));
          overflow: hidden;
        }

        .markdown-editor-wrapper .rc-md-navigation {
          background: hsl(var(--muted));
          border-bottom: 1px solid hsl(var(--border));
          padding: 8px 12px;
        }

        .markdown-editor-wrapper .rc-md-navigation .button-wrap button {
          color: hsl(var(--foreground));
          background: transparent;
          border: none;
          padding: 6px 10px;
          margin: 0 2px;
          border-radius: 0.375rem;
          transition: background-color 0.2s;
        }

        .markdown-editor-wrapper .rc-md-navigation .button-wrap button:hover {
          background: hsl(var(--accent));
        }

        .markdown-editor-wrapper .rc-md-navigation .button-wrap .button.active {
          background: hsl(var(--primary));
          color: hsl(var(--primary-foreground));
        }

        .markdown-editor-wrapper .rc-md-navigation .button-wrap .split {
          margin: 0 8px;
          border-left: 1px solid hsl(var(--border));
        }

        /* Editor section */
        .markdown-editor-wrapper .sec-md {
          background: hsl(var(--background));
        }

        .markdown-editor-wrapper .sec-md .input {
          background: hsl(var(--background));
          color: hsl(var(--foreground));
          padding: 16px;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 0.875rem;
          line-height: 1.6;
          border: none;
        }

        .markdown-editor-wrapper .sec-md .input:focus {
          outline: none;
        }

        /* Preview section */
        .markdown-editor-wrapper .sec-html {
          background: hsl(var(--background));
          padding: 16px;
          overflow-y: auto;
        }

        .markdown-editor-wrapper .sec-html .custom-html-style {
          color: hsl(var(--foreground));
          line-height: 1.6;
        }

        .markdown-editor-wrapper .sec-html .custom-html-style h1,
        .markdown-editor-wrapper .sec-html .custom-html-style h2,
        .markdown-editor-wrapper .sec-html .custom-html-style h3,
        .markdown-editor-wrapper .sec-html .custom-html-style h4 {
          color: hsl(var(--foreground));
          font-weight: 600;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
        }

        .markdown-editor-wrapper .sec-html .custom-html-style h1 {
          font-size: 1.875rem;
        }

        .markdown-editor-wrapper .sec-html .custom-html-style h2 {
          font-size: 1.5rem;
        }

        .markdown-editor-wrapper .sec-html .custom-html-style h3 {
          font-size: 1.25rem;
        }

        .markdown-editor-wrapper .sec-html .custom-html-style p {
          margin: 0.75em 0;
        }

        .markdown-editor-wrapper .sec-html .custom-html-style a {
          color: hsl(var(--primary));
          text-decoration: underline;
        }

        .markdown-editor-wrapper .sec-html .custom-html-style code {
          background: hsl(var(--muted));
          padding: 0.2em 0.4em;
          border-radius: 0.25rem;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 0.875em;
        }

        .markdown-editor-wrapper .sec-html .custom-html-style pre {
          background: hsl(var(--muted));
          padding: 1em;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin: 1em 0;
        }

        .markdown-editor-wrapper .sec-html .custom-html-style pre code {
          background: transparent;
          padding: 0;
        }

        .markdown-editor-wrapper .sec-html .custom-html-style ul,
        .markdown-editor-wrapper .sec-html .custom-html-style ol {
          margin: 0.75em 0;
          padding-left: 2em;
        }

        .markdown-editor-wrapper .sec-html .custom-html-style li {
          margin: 0.25em 0;
        }

        .markdown-editor-wrapper .sec-html .custom-html-style blockquote {
          border-left: 3px solid hsl(var(--primary));
          padding-left: 1em;
          margin: 1em 0;
          color: hsl(var(--muted-foreground));
          font-style: italic;
        }

        .markdown-editor-wrapper .sec-html .custom-html-style img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1em 0;
        }

        .markdown-editor-wrapper .sec-html .custom-html-style table {
          border-collapse: collapse;
          width: 100%;
          margin: 1em 0;
        }

        .markdown-editor-wrapper .sec-html .custom-html-style table th,
        .markdown-editor-wrapper .sec-html .custom-html-style table td {
          border: 1px solid hsl(var(--border));
          padding: 0.5em;
          text-align: left;
        }

        .markdown-editor-wrapper .sec-html .custom-html-style table th {
          background: hsl(var(--muted));
          font-weight: 600;
        }
      `}</style>
    </div>
  )
}
